import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Button, Text, Modal, Icon, FormAlert, IfHasPermission } from './index';
import { IconName } from './Icon/Icon.type';
import { Permissions } from '../constants';
import { versionControlApi } from '../services/versionControlApi';
import { GitRemoteSharePanel } from './GitRemoteSharePanel/GitRemoteSharePanel';

export interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
}

export interface SyncStatus {
  has_remote: boolean;
  ahead: number;
  behind: number;
  has_uncommitted_changes: boolean;
}

const DEFAULT_SYNC_STATUS: SyncStatus = { has_remote: false, ahead: 0, behind: 0, has_uncommitted_changes: false };

interface FileExplorerProps {
  projectId: string;
  onFileSelect?: (file: FileNode) => void;
  selectedFile?: string;
  className?: string;
  refreshTrigger?: boolean;
  initialSelectedFile?: string | null;
  onInitialFileSelected?: () => void;
  isDevelopmentInProgress?: boolean;
  readOnly?: boolean;
  permissions?: string[];
  syncStatus?: SyncStatus;
  onPullComplete?: () => void;
  onRefreshSync?: () => void;
}

const FileExplorer: React.FC<FileExplorerProps> = ({
  projectId,
  onFileSelect,
  selectedFile,
  className = '',
  refreshTrigger = false,
  initialSelectedFile,
  onInitialFileSelected,
  isDevelopmentInProgress = false,
  readOnly = false,
  permissions,
  syncStatus = DEFAULT_SYNC_STATUS,
  onPullComplete,
  onRefreshSync
}) => {
  const [files, setFiles] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [showPullConfirm, setShowPullConfirm] = useState(false);
  const [showPushCommit, setShowPushCommit] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  const [isGitRemotePanelOpen, setIsGitRemotePanelOpen] = useState(false);
  const [isGitRemotePanelClosing, setIsGitRemotePanelClosing] = useState(false);
  const [pullSuccessMessage, setPullSuccessMessage] = useState('');
  const [pullErrorMessage, setPullErrorMessage] = useState('');
  const [pushSuccessMessage, setPushSuccessMessage] = useState('');
  const [pushErrorMessage, setPushErrorMessage] = useState('');

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/dev-agent/projects/${projectId}/files`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const fileTree = response.data || [];
      setFiles(fileTree);

      // Start with all directories collapsed, but only if no initial file is being selected
      // (the initial file selection useEffect will expand the necessary directories)
      if (!initialSelectedFile) {
        setExpandedNodes(new Set());
      }
    } catch (err: any) {
      setError('Failed to load project files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadFiles();
    }
  }, [projectId, refreshTrigger]);

  const handleGitPullClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Fetch fresh sync status so has_uncommitted_changes is not stale
    let hasUncommitted = syncStatus.has_uncommitted_changes;
    try {
      const fresh = await versionControlApi.gitSyncStatus(projectId);
      hasUncommitted = fresh.has_uncommitted_changes;
      onRefreshSync?.();
    } catch {
      // Fall back to last known value
    }
    if (hasUncommitted) {
      setShowPullConfirm(true);
    } else {
      executePull(false);
    }
  };

  const executePull = async (discard: boolean) => {
    setPulling(true);
    setShowPullConfirm(false);
    setPullErrorMessage('');
    try {
      await versionControlApi.gitPull(projectId, discard);
      await loadFiles();
      onPullComplete?.();
      onRefreshSync?.();
      setPullSuccessMessage('Successfully pulled latest changes from remote');
    } catch (err: any) {
      console.error('Git pull failed:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to pull from remote. Please try again.';
      setPullErrorMessage(errorMsg);
    } finally {
      setPulling(false);
    }
  };

  const handleGitPushClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Fetch fresh sync status so has_uncommitted_changes is not stale
    let hasUncommitted = syncStatus.has_uncommitted_changes;
    try {
      const fresh = await versionControlApi.gitSyncStatus(projectId);
      hasUncommitted = fresh.has_uncommitted_changes;
      onRefreshSync?.();
    } catch {
      // Fall back to last known value
    }
    if (hasUncommitted) {
      setCommitMessage('');
      setShowPushCommit(true);
    } else {
      executePush();
    }
  };

  const executePush = async (message?: string) => {
    setPushing(true);
    setShowPushCommit(false);
    setPushErrorMessage('');
    try {
      await versionControlApi.gitPush(projectId, message);
      onRefreshSync?.();
      setPushSuccessMessage('Successfully pushed changes to remote');
    } catch (err: any) {
      console.error('Git push failed:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to push to remote. Please try again.';
      setPushErrorMessage(errorMsg);
    } finally {
      setPushing(false);
    }
  };

  const handleGitRemoteClick = () => {
    setIsGitRemotePanelOpen(true);
    setIsGitRemotePanelClosing(false);
  };

  const closeGitRemotePanel = () => {
    setIsGitRemotePanelClosing(true);
    setTimeout(() => {
      setIsGitRemotePanelOpen(false);
      setIsGitRemotePanelClosing(false);
    }, 300); // Wait for animation
  };

  // Helper function to normalize path separators (handle Windows backslash vs forward slash)
  const normalizePath = (path: string): string => {
    return path.replace(/\\/g, '/');
  };

  // Helper function to find a file node by path in the file tree
  const findFileByPath = (nodes: FileNode[], targetPath: string): FileNode | null => {
    const normalizedTarget = normalizePath(targetPath);
    
    for (const node of nodes) {
      const normalizedNodePath = normalizePath(node.path);
      
      if (normalizedNodePath === normalizedTarget) {
        return node;
      }
      if (node.type === 'directory' && node.children) {
        const found = findFileByPath(node.children, targetPath);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper function to get all parent directory paths for a given file path
  const getParentPaths = (filePath: string): string[] => {
    // Normalize path first (handle both forward and back slashes)
    const normalizedPath = normalizePath(filePath);
    const parts = normalizedPath.split('/');
    const parentPaths: string[] = [];
    
    // Build up parent paths progressively
    for (let i = 1; i < parts.length; i++) {
      parentPaths.push(parts.slice(0, i).join('/'));
    }
    
    return parentPaths;
  };

  // Handle initial file selection when initialSelectedFile changes
  useEffect(() => {
    // Skip if no initial file, files not loaded, or no callback
    if (!initialSelectedFile || files.length === 0 || !onFileSelect) {
      return;
    }
    
    // Find the file node
    const fileNode = findFileByPath(files, initialSelectedFile);
    
    if (fileNode) {
      // Auto-expand all parent directories
      // Get normalized parent paths first
      const parentPaths = getParentPaths(fileNode.path);
      
      // Map to actual node paths in the tree (handles path separator differences)
      const actualParentPaths = parentPaths
        .map(p => {
          const parentNode = findFileByPath(files, p);
          return parentNode ? parentNode.path : null;
        })
        .filter((p): p is string => p !== null);
      
      // Add parent paths to existing expanded nodes (don't replace - keep other folders open)
      setExpandedNodes(prev => new Set([...prev, ...actualParentPaths]));
      
      // Select the file
      onFileSelect(fileNode);
      
      // Notify parent that initial file has been selected (so it can clear the state)
      // This allows the same file to be selected again from chat
      onInitialFileSelected?.();
    }
  }, [initialSelectedFile, files, onFileSelect, onInitialFileSelected]);

  const handleToggle = (path: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedNodes(newExpanded);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.post(`/projects/${projectId}/download`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `project-${projectId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowDownloadConfirm(false);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  // TreeNode component
  const TreeNode: React.FC<{
    node: FileNode;
    level: number;
    onFileSelect?: (file: FileNode) => void;
    selectedFile?: string;
    onToggle: (path: string) => void;
    expandedNodes: Set<string>;
    isFirstDirectory?: boolean;
  }> = ({ node, level, onFileSelect, selectedFile, onToggle, expandedNodes, isFirstDirectory }) => {
    const isExpanded = expandedNodes.has(node.path);
    const isSelected = selectedFile === node.path;

    // Calculate padding based on level - consistent indentation per level
    // Base padding: 12px, then add 12px per level for consistent nesting
    const basePadding = 12; // px-bg-v2-lg
    const indentPerLevel = 12; // Consistent indent for each nesting level
    const leftPadding = basePadding + (level * indentPerLevel);
    
    // Use inline style for dynamic padding
    const paddingStyle = {
      paddingLeft: `${leftPadding}px`,
      paddingRight: '12px'
    };

    return (
      <div>
        <div
          className={`flex items-center gap-bg-v2-md py-bg-v2-sm rounded-bg-v2-sm hover:bg-bg-secondary cursor-pointer transition-colors ${
            isSelected ? 'bg-bg-quaternary' : ''
          }`}
          style={paddingStyle}
          onClick={() => {
            if (node.type === 'directory') {
              onToggle(node.path);
            } else if (onFileSelect) {
              onFileSelect(node);
            }
          }}
        >
          {node.type === 'directory' && (
            <>
              <Icon
                name={IconName.ChevronRight}
                className={`w-bg-v2-xl h-bg-v2-xl shrink-0 text-fg-tertiary transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                inheritColor
              />
              <Icon
                name={isExpanded ? IconName.FolderOpen : IconName.FolderClosed}
                className="text-text-secondary w-bg-v2-xl h-bg-v2-xl shrink-0"
                inheritColor
              />
            </>
          )}
          {node.type === 'file' && (
            <>
              {/* Invisible chevron spacer to align with folders */}
              <div className="w-bg-v2-xl h-bg-v2-xl shrink-0" />
              <Icon
                name={IconName.File}
                className="text-text-secondary w-bg-v2-xl h-bg-v2-xl shrink-0"
                variant={isSelected ? 'selected' : undefined} inheritColor
              />
            </>
          )}
          <div className="flex-1 min-w-0" title={node.name}>
            {isSelected ? (
              <Text variant="text-sm" className="truncate" weight="semibold" color="secondary">
                {node.name}
              </Text>
            ) : (
              <Text variant="text-sm" className="truncate" color="secondary">
                {node.name}
              </Text>
            )}
          </div>
        </div>
        {node.type === 'directory' && isExpanded && node.children && (
          <div>
            {node.children.map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                level={level + 1}
                onFileSelect={onFileSelect}
                selectedFile={selectedFile}
                onToggle={onToggle}
                expandedNodes={expandedNodes}
                isFirstDirectory={false}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Main container styles matching Figma design
  // 
  // PROPERTIES NOT FOUND IN tailwind.config.js:
  // 1. width: 247px / minWidth: 247px - No exact match in spacing scale
  //    Closest: bg-v2-10xl (128px) or bg-v2-11xl (160px) - both too small
  //    Consider adding custom width token if this value is used elsewhere
  // 2. w-[15px] h-[15px] (line 255) - No exact match in spacing scale
  //    Closest: bg-v2-xl (16px) - slightly larger
  //    Consider adding custom size token or use w-4 h-4 (16px standard Tailwind)
  const containerStyle: React.CSSProperties = {
    width: '223px',
    minWidth: '247px',
    height: '100%',
  };

  if (error) {
    return (
      <div 
        className={`flex flex-col items-start gap-bg-v2-md self-stretch bg-bg-primary border-r border-border-secondary rounded-bl-bg-v2-xl rounded-tl-bg-v2-xl overflow-hidden h-full relative ${className}`}
        style={containerStyle}
      >
        <div className="px-bg-v2-lg pt-bg-v2-xl pb-bg-v2-md border-b border-border-secondary bg-bg-primary flex items-center justify-between w-full">
          <Text variant="text-xs" weight="semibold" color="secondary" className="tracking-wide">
            Codebase
          </Text>
        </div>
        <div className="flex items-center justify-center h-bg-v2-10xl bg-bg-primary">
          <div className="text-center">
            <Text variant="text-sm" color="error" className="mb-bg-v2-xs">Error loading files</Text>
            <Button
              variant="link"
              size="small"
              onClick={loadFiles}
              label="Try again"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className={`flex flex-col items-start gap-bg-v2-md self-stretch bg-bg-primary border-r border-border-secondary rounded-bl-bg-v2-xl rounded-tl-bg-v2-xl overflow-hidden h-full relative ${className}`}
        style={containerStyle}
      >
        <div className="flex items-center justify-between w-full px-bg-v2-xl pt-bg-v2-xl pb-bg-v2-xl border-b border-border-primary">
          <div className="flex items-center gap-bg-v2-xl">
            <Icon name={IconName.Layout} size="sm" variant="secondary" />
            <Text variant="text-sm" weight="semibold" color="primary">
              Codebase
            </Text>
          </div>
          <div className="flex items-center gap-bg-v2-md">
            <IfHasPermission permissions={permissions} requires={Permissions.DownloadPermission}>
              <Button
                variant="ghost"
                size="very-small"
                onClick={() => setShowDownloadConfirm(true)}
                className="px-bg-v2-xs h-7 min-h-0 w-7"
                title="Download project"
                label={<Icon name={IconName.Download} className="w-bg-v2-xl h-bg-v2-xl text-fg-secondary" inheritColor />}
              />
            </IfHasPermission>
            <Button
              variant="ghost"
              size="very-small"
              onClick={loadFiles}
              disabled={loading}
              className="px-bg-v2-xs h-7 min-h-0 w-7"
              title="Refresh files"
              label={<Icon name={IconName.Reload} className={`w-bg-v2-xl h-bg-v2-xl text-fg-secondary ${loading ? 'animate-spin' : ''}`} inheritColor />}
            />
          </div>
        </div>

        <div className="w-full bg-bg-primary overflow-y-auto flex-1 min-h-0 px-bg-v2-lg pb-bg-v2-sm modern-scrollbar">
          {loading ? (
            // Skeleton animation while loading
            <div className="py-bg-v2-xxs pb-bg-v2-3xl space-y-bg-v2-xs">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="flex items-center gap-bg-v2-md py-bg-v2-sm px-bg-v2-lg rounded-bg-v2-sm hover:bg-bg-primary-hover">
                  {/* Icon skeleton */}
                  <div className="w-bg-v2-xl h-bg-v2-xl skeleton-shimmer rounded shrink-0" />
                  {/* Text skeleton */}
                  <div 
                    className="h-4 skeleton-shimmer rounded"
                    style={{ width: `${Math.random() * 40 + 60}%` }}
                  />
                </div>
              ))}
            </div>
          ) : files.length === 0 ? (
            <div className="flex items-center justify-center h-bg-v2-10xl">
              <div className="text-center">
                <Icon name={IconName.FolderClosed} className="mx-auto mb-bg-v2-xs text-text-tertiary w-6 h-6" />
                <Text variant="text-sm" color="secondary">No files found</Text>
              </div>
            </div>
          ) : (
            <div className="py-bg-v2-xxs pb-bg-v2-3xl">
              {files.map((file) => (
                <TreeNode
                  key={file.path}
                  node={file}
                  level={0}
                  onFileSelect={onFileSelect}
                  selectedFile={selectedFile}
                  onToggle={handleToggle}
                  expandedNodes={expandedNodes}
                  isFirstDirectory={file.type === 'directory'}
                />
              ))}
            </div>
          )}
        </div>

        {/* Git Sync Footer — only shown when a Gitea remote is configured */}
        {syncStatus.has_remote && (
          <>
            {/* Success/Error Messages for Git Operations - positioned above toolbar */}
            {pullSuccessMessage && (
              <div className="w-full px-bg-v2-lg">
                <FormAlert
                  message={pullSuccessMessage}
                  variant="success"
                  autoDismiss={false}
                  className="w-full !p-bg-v2-sm !text-bg-v2-xs !gap-bg-v2-xs"
                  autoDismissTimeout={4000}
                  onDismiss={() => setPullSuccessMessage('')}
                />
              </div>
            )}

            {pullErrorMessage && (
              <div className="w-full px-bg-v2-lg">
                <FormAlert
                  message={pullErrorMessage}
                  variant="error"
                  className="w-full !p-bg-v2-sm !text-bg-v2-xs !gap-bg-v2-xs"
                  onDismiss={() => setPullErrorMessage('')}
                />
              </div>
            )}

            {pushSuccessMessage && (
              <div className="w-full px-bg-v2-lg">
                <FormAlert
                  message={pushSuccessMessage}
                  variant="success"
                  autoDismiss={true}
                  className="w-full !p-bg-v2-sm !text-bg-v2-xs !gap-bg-v2-xs"
                  autoDismissTimeout={4000}
                  onDismiss={() => setPushSuccessMessage('')}
                />
              </div>
            )}

            {pushErrorMessage && (
              <div className="w-full px-bg-v2-lg">
                <FormAlert
                  message={pushErrorMessage}
                  variant="error"
                  className="w-full !p-bg-v2-sm !text-bg-v2-xs !gap-bg-v2-xs"
                  onDismiss={() => setPushErrorMessage('')}
                />
              </div>
            )}
          </>
        )}
        {syncStatus.has_remote && (
          <div 
            className="w-full border-t border-border-primary flex items-center shrink-0"
          >
            {/* Git Checkout */}
            <button
              onClick={handleGitRemoteClick}
              className="flex items-center gap-bg-v2-xs px-bg-v2-lg py-bg-v2-sm hover:bg-bg-primary-hover transition-colors"
              title="Git remote checkout"
            >
              <Icon name={IconName.Share} size="sm" className="text-fg-secondary shrink-0" inheritColor />
              <Text variant="text-xs" color="secondary" weight="medium">
                Checkout
              </Text>
            </button>

            <div className="w-px self-stretch bg-border-primary" />

            {/* Pull — sync down from remote */}
            <button
              onClick={handleGitPullClick}
              disabled={pulling || isDevelopmentInProgress}
              title={isDevelopmentInProgress ? "Cannot pull while development is in progress" : "Pull latest from remote"}
              className="flex items-center gap-bg-v2-xs px-bg-v2-lg py-bg-v2-sm hover:bg-bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon
                name={IconName.Pull}
                size="sm"
                className={`text-fg-secondary ${pulling ? 'animate-bounce' : ''}`}
                inheritColor
              />
              <Text variant="text-xs" color="secondary" weight="medium">Pull</Text>
              {syncStatus.behind > 0 && (
                <span className="bg-bg-warning-primary text-fg-warning-primary rounded-full text-[9px] leading-none min-w-[14px] h-[14px] flex items-center justify-center font-semibold px-[3px]">
                  {syncStatus.behind}
                </span>
              )}
            </button>

            {!readOnly && (
              <>
                <div className="w-px self-stretch bg-border-primary" />

                {/* Push — sync up to remote */}
                <button
                  onClick={handleGitPushClick}
                  disabled={pushing || isDevelopmentInProgress}
                  title={isDevelopmentInProgress ? "Cannot push while development is in progress" : "Push local commits to remote"}
                  className="flex items-center gap-bg-v2-xs px-bg-v2-lg py-bg-v2-sm hover:bg-bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon
                    name={IconName.Push}
                    size="sm"
                    className={`text-fg-secondary ${pushing ? 'animate-bounce' : ''}`}
                    inheritColor
                  />
                  <Text variant="text-xs" color="secondary" weight="medium">Push</Text>
                  {syncStatus.ahead > 0 && (
                    <span className="bg-bg-brand-primary-alt text-fg-brand-primary rounded-full text-[9px] leading-none min-w-[14px] h-[14px] flex items-center justify-center font-semibold px-[3px]">
                      {syncStatus.ahead}
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <GitRemoteSharePanel
        projectId={projectId}
        isOpen={isGitRemotePanelOpen}
        isClosing={isGitRemotePanelClosing}
        onClose={closeGitRemotePanel}
      />

      <Modal
        isOpen={showDownloadConfirm}
        onClose={() => setShowDownloadConfirm(false)}
        title="Download Project"
        size="sm"
        onSubmit={handleDownload}
        onCancel={() => setShowDownloadConfirm(false)}
        submitLabel={downloading ? 'Downloading...' : 'Download'}
        submitLoading={downloading}
        submitDisabled={downloading}
      >
        <div className="space-y-bg-v2-md">
          <Text color="secondary">
            Are you sure you want to download the project files as a ZIP?
          </Text>
          <div className="bg-bg-secondary-subtle p-bg-v2-sm rounded-bg-v2-md border border-border-secondary">
            <Text variant="text-sm" weight="medium" color="secondary">
              Check the README.txt file in the downloaded project for setup steps
            </Text>
          </div>
        </div>
      </Modal>

      {/* Pull Confirmation — uncommitted changes warning */}
      <Modal
        isOpen={showPullConfirm}
        onClose={() => setShowPullConfirm(false)}
        title="Uncommitted Changes Detected"
        size="sm"
        onSubmit={() => executePull(true)}
        onCancel={() => setShowPullConfirm(false)}
        submitLabel={pulling ? 'Pulling...' : 'Discard & Pull'}
        submitLoading={pulling}
        submitDisabled={pulling}
      >
        <div className="space-y-bg-v2-md">
          <Text color="secondary">
            You have uncommitted local changes. Pulling from the server will <Text weight="semibold" color="error" className="inline">discard all local changes</Text> and replace them with the remote version.
          </Text>
          <div className="bg-bg-warning-primary p-bg-v2-sm rounded-bg-v2-md border border-utility-warning-300">
            <Text variant="text-sm" weight="medium" color="warning">
              This action cannot be undone. Consider committing and pushing your changes first.
            </Text>
          </div>
        </div>
      </Modal>

      {/* Push Commit — ask for commit message before pushing */}
      <Modal
        isOpen={showPushCommit}
        onClose={() => setShowPushCommit(false)}
        title="Commit & Push"
        size="sm"
        onSubmit={() => executePush(commitMessage.trim())}
        onCancel={() => setShowPushCommit(false)}
        submitLabel={pushing ? 'Pushing...' : 'Commit & Push'}
        submitLoading={pushing}
        submitDisabled={pushing || !commitMessage.trim()}
      >
        <div className="space-y-bg-v2-md">
          <Text color="secondary">
            You have uncommitted changes. Enter a commit message to commit and push them to the remote.
          </Text>
          <div>
            <Text variant="text-sm" weight="medium" color="secondary" className="mb-bg-v2-xs">
              Commit Message
            </Text>
            <input
              type="text"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && commitMessage.trim()) {
                  executePush(commitMessage.trim());
                }
              }}
              placeholder="Describe your changes..."
              autoFocus
              className="w-full px-bg-v2-md py-bg-v2-sm rounded-bg-v2-md border border-border-secondary bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-border-brand placeholder:text-text-tertiary"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FileExplorer;